// "Where else is this person rostered?" — one employee's whole month, ACROSS
// every branch.
//
// Why this exists. Xano allows exactly one active employee_timetable row per
// (employee, date) for the entire clinic, so a person is always in exactly one
// of four states on a given day: rostered here, rostered at another branch, on
// leave, or free. The roster screens are branch-scoped (an all-branch month is
// thousands of rows), which means the rows they hold can only ever answer the
// first and third. Without this lookup the drawer would call a day free that is
// already taken at another branch, and the save would fail on Xano's own
// precondition after the operator had filled the form in.
//
// One request per employee-month, cached for the session: a month is at most 31
// rows, so a person is one small page. The cache is shared, so the matrix
// warming it also answers the drawer instantly.
import { apiGet } from './api.js'

const cache = new Map()     // `${employeeId}|${start}|${end}` → rows[]
const inflight = new Map()  // same key → Promise<rows[]>

const keyOf = (employeeId, range) => `${Number(employeeId) || 0}|${range?.start_date}|${range?.end_date}`

const asList = (r) => (Array.isArray(r) ? r : (r?.items || []))

/**
 * Every row this employee holds in `range`, any branch — or **null** when the
 * lookup could not run.
 *
 * null rather than []: an empty list means "this person is free all month",
 * which is the one answer a failed request must never be allowed to give. The
 * callers fall back to their weaker check and say so.
 *
 * per_page 100 covers a calendar month with room to spare — one page, always,
 * so there is no sweep to get wrong.
 */
export function fetchEmployeeMonth(employeeId, range) {
  const id = Number(employeeId) || 0
  if (!id || !range?.start_date || !range?.end_date) return Promise.resolve(null)
  const key = keyOf(id, range)
  if (cache.has(key)) return Promise.resolve(cache.get(key))
  if (inflight.has(key)) return inflight.get(key)

  const p = apiGet('/api/hr/schedule/employee', {
    employee_id: id,
    start_date: range.start_date,
    end_date: range.end_date,
    per_page: 100,
  })
    .then((r) => {
      const rows = asList(r).filter((x) => x?.active !== false)
      cache.set(key, rows)
      return rows
    })
    .catch(() => null)          // unknown, NOT "free" — see the note above
    .finally(() => inflight.delete(key))

  inflight.set(key, p)
  return p
}

// Read-through for a person already fetched, without triggering a fetch.
export const peekEmployeeMonth = (employeeId, range) => cache.get(keyOf(employeeId, range)) || null

/**
 * Warm the cache for a list of employees, a few at a time.
 *
 * Bounded concurrency rather than Promise.all: a branch roster is 8–11 people
 * but อารีย์ is 46, and 46 simultaneous requests through the proxy would be a
 * self-inflicted stampede for a decoration.
 */
export async function fetchManyEmployeeMonths(employeeIds, range, { concurrency = 6 } = {}) {
  const ids = [...new Set((employeeIds || []).map(Number).filter(Boolean))]
  let i = 0
  const workers = Array.from({ length: Math.min(concurrency, ids.length) }, async () => {
    for (;;) {
      const idx = i++
      if (idx >= ids.length) return
      await fetchEmployeeMonth(ids[idx], range)
    }
  })
  await Promise.all(workers)
  // Only the ids that actually resolved. An id left OUT reads as "not looked
  // up" everywhere downstream, which is the truth when its request failed.
  const out = new Map()
  for (const id of ids) {
    const rows = cache.get(keyOf(id, range))
    if (rows) out.set(id, rows)
  }
  return out
}

// Any write to the roster invalidates every cached month — a moved shift can
// change two people's months at once, and stale "free" is the one answer this
// lookup must never give.
export function clearEmployeeMonthCache() {
  cache.clear()
  inflight.clear()
}

/**
 * The row that already occupies (employee, date) at ANOTHER branch, or null.
 * `branchId` is the branch on screen; rows belonging to it are the caller's own
 * data and are not a foreign conflict.
 */
export function foreignRowOn(rows, date, branchId) {
  const day = String(date || '').slice(0, 10)
  const bid = Number(branchId) || 0
  return (rows || []).find((r) => (
    String(r?.date || '').slice(0, 10) === day && Number(r?.branch_id) !== bid
  )) || null
}
